import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { collaborateursService, usersService, servicesService } from "@/services";
import {
  Edit,
  KeyRound,
  MoreHorizontal,
  Shield,
  ShieldOff,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { AppRole, Collaborateur, CollaborateurUserStatus, Service } from "@/types";

const collaborateurSchema = z
  .object({
    nom: z.string().min(1, "Le nom est requis"),
    email: z.string().email("Email invalide").optional().or(z.literal("")),
    serviceId: z.string().optional(),
    estActif: z.boolean().default(true),
    role: z.enum(["admin", "utilisateur"]).default("utilisateur"),
    initialPassword: z.string().optional().or(z.literal("")),
  })
  .superRefine((data, ctx) => {
    if (data.initialPassword && data.initialPassword.length > 0 && data.initialPassword.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["initialPassword"],
        message: "Minimum 8 caractères",
      });
    }
  });

type CollaborateurFormData = z.infer<typeof collaborateurSchema>;

export default function GestionCollaborateurs() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [collaborateurs, setCollaborateurs] = useState<Collaborateur[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollaborateur, setEditingCollaborateur] = useState<Collaborateur | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedCollaborateur, setSelectedCollaborateur] = useState<Collaborateur | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const form = useForm<CollaborateurFormData>({
    resolver: zodResolver(collaborateurSchema),
    defaultValues: {
      nom: "",
      email: "",
      serviceId: "",
      estActif: true,
      role: "utilisateur",
      initialPassword: "",
    },
  });

  const watchedEmail = form.watch("email");
  const watchedActif = form.watch("estActif");

  const loadData = async () => {
    try {
      setLoading(true);
      const [servicesData, collabData] = await Promise.all([
        servicesService.list(),
        collaborateursService.list(),
      ]);
      setServices(servicesData);
      setCollaborateurs(collabData);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les données",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === "admin") {
      loadData();
    }
  }, [userRole]);

  const handleSubmit = async (data: CollaborateurFormData) => {
    const hasEmail = Boolean(data.email && data.email.trim());
    const needsPassword =
      hasEmail && data.estActif && (!editingCollaborateur || !editingCollaborateur.userId);

    if (needsPassword && (!data.initialPassword || data.initialPassword.length < 8)) {
      form.setError("initialPassword", {
        message: "Requis pour créer le compte (minimum 8 caractères)",
      });
      return;
    }

    try {
      if (editingCollaborateur) {
        await collaborateursService.update(editingCollaborateur.id, {
          nom: data.nom,
          email: data.email || undefined,
          serviceId: data.serviceId || undefined,
          estActif: data.estActif,
          role: data.role,
          initialPassword: data.initialPassword || undefined,
        });
        toast({ title: "Succès", description: "Collaborateur mis à jour" });
      } else {
        await collaborateursService.create({
          nom: data.nom,
          email: data.email || undefined,
          serviceId: data.serviceId || undefined,
          estActif: data.estActif,
          role: data.role,
          initialPassword: data.initialPassword || undefined,
        });
        toast({
          title: "Succès",
          description: hasEmail
            ? "Collaborateur créé avec son compte utilisateur"
            : "Collaborateur créé (sans compte)",
        });
      }

      setIsDialogOpen(false);
      form.reset();
      setEditingCollaborateur(null);
      loadData();
    } catch (error: any) {
      console.error("Erreur:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error?.response?.data?.error || "Une erreur est survenue",
      });
    }
  };

  const handleEdit = (collaborateur: Collaborateur) => {
    setEditingCollaborateur(collaborateur);
    form.reset({
      nom: collaborateur.nom,
      email: collaborateur.email || "",
      serviceId: collaborateur.service?.id || "",
      estActif: collaborateur.estActif,
      role: collaborateur.userRole || "utilisateur",
      initialPassword: "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCollaborateur) return;
    try {
      if (selectedCollaborateur.userId) {
        try {
          await usersService.delete(selectedCollaborateur.userId);
        } catch (userError) {
          console.error("Error deleting user:", userError);
        }
      }
      await collaborateursService.delete(selectedCollaborateur.id);
      toast({ title: "Succès", description: "Collaborateur supprimé" });
      setDeleteConfirmOpen(false);
      setSelectedCollaborateur(null);
      loadData();
    } catch (error: any) {
      console.error("Erreur:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error?.response?.data?.error || "Impossible de supprimer",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!selectedCollaborateur?.userId || !newPassword) return;
    if (newPassword.length < 8) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 8 caractères",
      });
      return;
    }
    try {
      await usersService.updatePassword(selectedCollaborateur.userId, newPassword);
      toast({ title: "Succès", description: "Mot de passe réinitialisé" });
      setResetPasswordDialogOpen(false);
      setNewPassword("");
      setSelectedCollaborateur(null);
      loadData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error?.response?.data?.error || "Impossible de réinitialiser le mot de passe",
      });
    }
  };

  const handleToggleBan = async (collaborateur: Collaborateur) => {
    if (!collaborateur.userId) return;
    try {
      const isBanned = collaborateur.userStatus === "banned";
      await usersService.updateStatus(collaborateur.userId, !isBanned);
      toast({
        title: "Succès",
        description: isBanned ? "Utilisateur débloqué" : "Utilisateur bloqué",
      });
      loadData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error?.response?.data?.error || "Impossible de modifier le statut",
      });
    }
  };

  const getStatusBadge = (status: CollaborateurUserStatus | undefined, hasEmail: boolean) => {
    if (!status) {
      return (
        <Badge variant="outline" title={hasEmail ? "Aucun compte lié" : "Pas d'email renseigné"}>
          Sans compte
        </Badge>
      );
    }
    if (status === "banned") return <Badge variant="destructive">Bloqué</Badge>;
    if (status === "unverified") return <Badge variant="secondary">Non vérifié</Badge>;
    return <Badge className="bg-green-500 hover:bg-green-600">Actif</Badge>;
  };

  if (userRole !== "admin") {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Accès refusé</h2>
            <p className="text-muted-foreground">
              Vous devez être administrateur pour accéder à cette page.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <p>Chargement...</p>
        </div>
      </Layout>
    );
  }

  const showPasswordField =
    Boolean(watchedEmail && watchedEmail.trim()) &&
    watchedActif &&
    (!editingCollaborateur || !editingCollaborateur.userId);

  return (
    <Layout>
      <div className="space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-8 w-8" />
              Gestion des Collaborateurs
            </h1>
            <p className="text-muted-foreground mt-2">
              Un collaborateur avec email et statut <em>Actif</em> possède automatiquement un compte utilisateur.
            </p>
          </div>
          <Button
            onClick={() => {
              setEditingCollaborateur(null);
              form.reset({
                nom: "",
                email: "",
                serviceId: "",
                estActif: true,
                role: "utilisateur",
                initialPassword: "",
              });
              setIsDialogOpen(true);
            }}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Nouveau collaborateur
          </Button>
        </div>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collaborateurs.map((collab) => (
                <TableRow key={collab.id}>
                  <TableCell className="font-medium">{collab.nom}</TableCell>
                  <TableCell>{collab.email || collab.userEmail || "-"}</TableCell>
                  <TableCell>{collab.service?.nom || "-"}</TableCell>
                  <TableCell>
                    {collab.userRole ? (
                      <Badge variant={collab.userRole === "admin" ? "default" : "secondary"}>
                        {collab.userRole === "admin" ? "Admin" : "Utilisateur"}
                      </Badge>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(collab.userStatus, Boolean(collab.email))}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(collab)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                        {collab.userId && (
                          <>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedCollaborateur(collab);
                                setResetPasswordDialogOpen(true);
                              }}
                            >
                              <KeyRound className="h-4 w-4 mr-2" />
                              Réinitialiser le mot de passe
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleBan(collab)}>
                              {collab.userStatus === "banned" ? (
                                <>
                                  <Shield className="h-4 w-4 mr-2 text-green-600" />
                                  Débloquer
                                </>
                              ) : (
                                <>
                                  <ShieldOff className="h-4 w-4 mr-2 text-destructive" />
                                  Bloquer
                                </>
                              )}
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => {
                            setSelectedCollaborateur(collab);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {collaborateurs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Aucun collaborateur
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingCollaborateur ? "Modifier" : "Nouveau"} collaborateur
              </DialogTitle>
              <DialogDescription>
                {editingCollaborateur
                  ? "Modifiez les informations du collaborateur"
                  : "Un compte utilisateur sera créé automatiquement si email + Actif + mot de passe sont renseignés."}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="nom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormDescription>
                        Requis pour permettre la connexion et l'assignation à des AO.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="serviceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Service</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un service" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.nom}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rôle</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value as AppRole)}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="utilisateur">Utilisateur</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="estActif"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Actif</FormLabel>
                        <FormDescription>
                          {editingCollaborateur?.userId
                            ? "Désactiver bloque le compte ; réactiver le débloque."
                            : "Si Actif + email + mot de passe, un compte sera créé."}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {showPasswordField && (
                  <FormField
                    control={form.control}
                    name="initialPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mot de passe initial *</FormLabel>
                        <FormControl>
                          <Input {...field} type="password" placeholder="Minimum 8 caractères" />
                        </FormControl>
                        <FormDescription>
                          Communiquez-le au collaborateur ; il pourra le changer ensuite.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingCollaborateur ? "Mettre à jour" : "Créer"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
              <DialogDescription>
                Nouveau mot de passe pour {selectedCollaborateur?.nom}. Ce reset débloque aussi le compte s'il était bloqué.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setResetPasswordDialogOpen(false);
                  setNewPassword("");
                  setSelectedCollaborateur(null);
                }}
              >
                Annuler
              </Button>
              <Button onClick={handleResetPassword}>Réinitialiser</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer {selectedCollaborateur?.nom} ?
                {selectedCollaborateur?.userId && (
                  <span className="block mt-2 text-destructive font-medium">
                    Le compte utilisateur associé sera également supprimé.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setSelectedCollaborateur(null);
                }}
              >
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
